// Header drawer / sidebar integration.
//
// Pairs with three CSS rules:
//   body.drawer-open .custom-width-sidebar { display: none !important }
//   body:has(header-sidebar[open]) .custom-width-sidebar { display: none !important }
//   body.sub-panel-open .header-sidebar__main-panel { display: none !important }
//
// Two distinct visual bugs this addresses:
//
// (a) Desktop primary nav leaking through the open mobile drawer at
//     >=1250px viewports (orientation change while drawer open).
//     Fixed by hiding .custom-width-sidebar when the drawer's open
//     attribute is set + by auto-closing the drawer at >=1250px.
//
// (b) Sidebar's main panel showing through an active sub-panel at
//     >=1000px viewports (Customizer, tablet landscape). The bundle
//     positions the collapsible-panel scroller `position: absolute`
//     over the main-panel area but never hides the main-panel, so
//     both render visibly. Fixed by hiding .header-sidebar__main-panel
//     when the collapsible-panel has aria-activedescendant set.
//
// CSS :has() variants exist for both, but the Shopify Theme Editor
// preview iframe doesn't always honour them, so each condition is
// also reflected as a body class via MutationObserver below.

(() => {
  const DESKTOP_QUERY = '(min-width: 1250px)';
  const DRAWER_ID = 'sidebar-menu';
  const DRAWER_OPEN_CLASS = 'drawer-open';
  const SUB_PANEL_OPEN_CLASS = 'sub-panel-open';

  function findDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function findCollapsiblePanel() {
    return document.querySelector('.header-sidebar__collapsible-panel');
  }

  function drawerIsOpen(drawer) {
    if (!drawer) return false;
    if (drawer.hasAttribute('open')) return true;
    if (typeof drawer.open === 'boolean' && drawer.open) return true;
    return false;
  }

  function subPanelIsActive(panel) {
    if (!panel) return false;
    const id = panel.getAttribute('aria-activedescendant');
    return typeof id === 'string' && id.length > 0;
  }

  function syncBodyClasses(drawer, panel) {
    if (!document.body) return;
    document.body.classList.toggle(DRAWER_OPEN_CLASS, drawerIsOpen(drawer));
    document.body.classList.toggle(SUB_PANEL_OPEN_CLASS, subPanelIsActive(panel));
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    if (typeof drawer.hide === 'function') {
      drawer.hide();
    } else if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      drawer.removeAttribute('open');
    }
  }

  function watch(drawer, panel) {
    syncBodyClasses(drawer, panel);

    if (drawer) {
      const drawerObserver = new MutationObserver(() => syncBodyClasses(drawer, panel));
      drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      drawer.addEventListener('dialog:after-show', () => syncBodyClasses(drawer, panel));
      drawer.addEventListener('dialog:after-hide', () => syncBodyClasses(drawer, panel));
    }

    if (panel) {
      const panelObserver = new MutationObserver(() => syncBodyClasses(drawer, panel));
      panelObserver.observe(panel, { attributes: true, attributeFilter: ['aria-activedescendant'] });
    }
  }

  function handleDesktop(event) {
    if (!event.matches) return;
    const drawer = findDrawer();
    if (drawerIsOpen(drawer)) closeDrawer(drawer);
  }

  function init() {
    watch(findDrawer(), findCollapsiblePanel());

    if (typeof window.matchMedia === 'function') {
      const mql = window.matchMedia(DESKTOP_QUERY);
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handleDesktop);
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(handleDesktop);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
